<template>
	<Loading v-show="loading" />
	<div class="relative flex h-full w-full p-3" v-bind="$attrs">
		<teleport v-if="openModalAccept" to="body">
			<div class="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 transition-opacity"></div>
			<div class="fixed inset-0 z-60 w-full h-screen overflow-y-auto">
				<div class="flex min-h-full justify-center text-center items-center p-1">
					<div class="relative transform bg-white rounded-md text-left shadow-xl transition-all w-[78%] md:w-[445px] p-3 pb-[18px]">
						<div class="flex flex-col gap-3 md:gap-[17px] items-center">
							<div class="flex flex-col items-center w-full">
								<div class="w-full flex justify-end">
									<button aria-label="Cerrar" @click="handleModalAccept" class="text-primary h-[18px] md:h-[26px] w-[18px] md:w-[26px] flex items-center justify-center">
										<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="#2563eb"><path d="M10.5859 12L2.79297 4.20706L4.20718 2.79285L12.0001 10.5857L19.793 2.79285L21.2072 4.20706L13.4143 12L21.2072 19.7928L19.793 21.2071L12.0001 13.4142L4.20718 21.2071L2.79297 19.7928L10.5859 12Z"></path></svg>
									</button>
								</div>
								<img :src="modalImage" alt="" class="w-[65%] max-w-[200px] md:max-w-[268px] h-auto object-cover" />
							</div>
							<h2 class="text-base md:text-2xl font-bold">¡Enhorabuena!</h2>
							<p class="text-sm md:text-xl text-gray-700 text-center">Tu lead ha sido <strong>reservado con éxito</strong>, en breve recibirás un email con la confirmación de compra.</p>
							<Button color="primary" @click="handleModalAccept" class="font-bold text-sm md:text-[22px] py-2 px-[15px] md:py-[11px] md:px-[22px] leading-[100%]">Continuar</Button>
						</div>
					</div>
				</div>
			</div>
		</teleport>

		<teleport v-if="lockError" to="body">
			<div class="relative z-40" aria-labelledby="modal-title" role="dialog" aria-modal="true">
				<div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
				<div class="fixed inset-0 z-10 w-full h-screen overflow-y-auto">
					<div class="flex min-h-full justify-center text-center items-center p-1" @click="lockError = null">
						<div class="relative transform rounded-sm bg-white text-left shadow-xl transition-all w-full lg:w-3/4 xl:w-1/4" @click.stop>
							<button class="absolute top-2 right-2" aria-label="Cerrar" @click="lockError = null">
								<icon name="ri-close-line" class="opacity-70 w-5 h-5" />
							</button>
							<div class="bg-gray-50 px-4 py-6 text-center flex flex-col gap-4">
								<h2 class="text-lg font-semibold leading-6 text-gray-900 flex items-center justify-center gap-1.5">
									<icon name="ri-alert-line" class="opacity-70 w-5 h-5 text-yellow-500" />
									Advertencia
								</h2>
								<p class="text-sm text-gray-800">{{ lockError }}</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</teleport>
		<div v-if="lead" class="grow flex flex-col gap-[25px] md:translate-x-0 transform transition-transform duration-300 ease-in-out translate-x-0">
			<LeadConditions v-if="showConditionsModal" @abort="showConditionsModal = false" @success="handleAcceptConditions" />
			<BillingDataModal v-if="showBillingModal && !user.config.hasBillData" @abort="showBillingModal = false" @success="handleBillingSuccess" />
			<div v-if="lead.price && lead.price > 0" class="relative rounded-lg overflow-hidden">
				<div class="relative z-10 px-[25px] py-5 flex flex-col gap-[25px] items-center text-center">
					<h2 class="font-semibold text-[23px] leading-[125%] md:text-[36px] md:leading-[100%] text-white uppercase">Alquila ahora este lead al mejor precio</h2>
					<div class="bg-black/50 px-5 py-2.5 rounded-[4px] border border-primary-600 flex items-center justify-center">
						<span class="flex items-end font-extrabold text-[45px] md:text-[65px] leading-[100%] bg-gradient-to-r from-primary-600 to-[#5964AC] bg-clip-text text-transparent">{{ lead.price }}<span class="font-bold text-[30px] md:text-[40px] leading-[115%]">€</span></span>
					</div>
				</div>
				<img :src="leadPriceDesktop" alt="" class="hidden md:block absolute top-0 left-0 w-full h-full object-cover" />
				<img :src="leadPriceMobile" alt="" class="block md:hidden absolute top-0 left-0 w-full h-full object-cover" />
			</div>

			{{ lead.distance }}

			<ContractSummaryEnergy :distance="props.distance" v-if="!isFinance" :contract="lead.contract" :price="lead.price" :deactivateAt="lead.deactivateAt ? lead.deactivateAt.toString() : undefined" :lead="lead" :project="props.project" />
			<ContractSummaryFinance :distance="props.distance" v-if="isFinance" :contract="lead.contract" />
			<LeadAddress :lead="lead" v-if="isFinance && lead.loc && lead.contract.address.full" :project="props.project" class="!rounded-sm !border" />
			<Comparative v-if="!isFinance && lead.contract.cups && lead.contract.rate" :contract="lead.contract" :hasPrice="!!(lead.price && lead.price > 0)" />

			<div class="flex flex-col gap-[15px] items-center justify-end">
				<span v-if="lead.price && lead.price > 0" class="text-primary-600 font-semibold text-[18px] md:text-[22px] leading-[125%]">No dejes pasar esta oportunidad</span>
				<Button v-if="!lead.locked" class="w-full md:w-auto text-[18px] md:text-[22px] leading-[84%] px-5 py-[11px] md:py-[15px]" @click="startLockProcess()" :loading="locking" loadingLabel="Bloqueando..." :disabled="locking || lead.locked">
					<icon name="ri-lock-line" class="opacity-70 w-5 h-5 mr-1" />
					{{ lead.price && lead.price > 0 ? 'Alquilar lead' : 'Bloquear lead' }}
				</Button>
			</div>

			<div v-if="lead.tempLockExpiresAt" class="bg-black/10 px-3 py-2.5 rounded-lg">
				<p class="text-xs md:text-sm text-gray-800"><b>Reservado temporalmente: </b> si decides no bloquearlo, {{ dayjs(lead.tempLockExpiresAt).fromNow() }} este lead estará disponible para que otro comercial pueda gestionarlo.</p>
			</div>
		</div>

		<div v-else-if="error" class="text-xs text-gray-800 w-full">
			<Banner :open="true" type="error" fixed>
				<p>{{ props.lockOnFetch ? 'Este lead ya está bloqueado por otro comercial' : 'No se ha podido cargar el lead' }}</p>
			</Banner>
		</div>
	</div>
</template>

<script setup lang="ts">
import LeadAddress from '@/apps/go/components/leads/LeadAddress.vue'
import { dayjs } from '@/services/dayjs'
import { user } from '@/user'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import Banner from '../../components/Banner.vue'
import Button from '../../components/Button.vue'
import BillingDataModal from '../../components/leads/BillingDataModal.vue'
import Comparative from '../../components/leads/Comparative.vue'
import ContractSummaryEnergy from '../../components/leads/ContractSummaryEnergy.vue'
import ContractSummaryFinance from '../../components/leads/ContractSummaryFinance.vue'
import LeadConditions from '../../components/leads/LeadConditions.vue'
import Loading from '../../components/Loading.vue'
import { Lead } from '../../interfaces/Lead'
import { del, get, post, put } from '../../services/api'
import leadPriceDesktop from '@/apps/go/images/leads/bg-price-desktop.png'
import leadPriceMobile from '@/apps/go/images/leads/bg-price-mobile.png'
import modalImage from '@/apps/go/images/leads/lead-modal.png'

const isFinance = import.meta.env.VITE_PRODUCT == 'finance'
const props = defineProps<{ _id: string; lockOnFetch: boolean; project: string; distance?: number; coordinates?: any }>()
const lead = ref<Lead | null>(null)
const lockData = ref<any>(null)
const loading = ref(false)
const locking = ref(false)
const emit = defineEmits(['lock'])
const openModalAccept = ref(false)

const error = ref<boolean>(false)
const lockError = ref<string | null>(null)

const showConditionsModal = ref(false)
const showBillingModal = ref(false)

const startLockProcess = async () => {
	if (lead.value?.price > 0) {
		if (!user.value.config.hasBillData) {
			showBillingModal.value = true
			return
		}
		showConditionsModal.value = true
		return
	}
	await lock()
}

const handleBillingSuccess = async () => {
	showBillingModal.value = false
	showConditionsModal.value = true
}

const handleAcceptConditions = async () => {
	showConditionsModal.value = false
	await lock()
}

const fetch = async () => {
	loading.value = true

	try {
		if (props.lockOnFetch) {
			const { data } = await post({ name: 'lead.temp.lock', params: { project: props.project, _id: props._id } }, {}, undefined, props.coordinates)
			lead.value = data
		} else {
			const { data } = await get({ name: 'lead.preview', params: { project: props.project, _id: props._id } })
			lead.value = data
		}
	} catch (err) {
		error.value = true
	}

	loading.value = false
}

const lock = async () => {
	try {
		loading.value = true
		locking.value = true
		const { data } = await put({ name: 'lead.lock', params: { project: props.project, _id: props._id } }, undefined, undefined, props.coordinates)
		if (lead.value?.price > 0) {
			lockData.value = data
			openModalAccept.value = true
		} else {
			emit('lock', data)
		}
	} catch (err) {
		if (err.message && err.errors && err.errors.length > 0 && err.errors.some(e => e.type === 'invalidLead')) {
			lockError.value = err.message
		} else {
			alert('Ha ocurrido un error')
		}
	} finally {
		loading.value = false
		locking.value = false
	}
}

const unlock = async () => {
	try {
		if (lead.value && props.lockOnFetch) {
			await del({ name: 'lead.temp.lock', params: { project: props.project, _id: lead.value._id } })
		}
	} catch (error) {
		alert('Ha ocurrido un error')
	}
}

const handleModalAccept = () => {
	const data = lockData.value
	if (lockData.value) {
		emit('lock', data)
	}
	openModalAccept.value = false
}

onUnmounted(async () => {
	await unlock()
	lead.value = null
})

onMounted(() => {
	fetch()
})

watch(
	() => props._id,
	_id => {
		unlock()
		if (props._id) {
			fetch()
		}
	}
)
</script>
